import { HpButton, Icon } from '@hp/ui';
import type { Permission } from '@hp/domain';
import type { PipelineState } from './queries';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { usePipelineText } from './messages';
import { KIND_PERMISSION } from './stage-meta';
import { usePipelineAction } from './use-pipeline-action';

// --- a) Next-step banner ---

export function NextStepBanner({ state, can }: { state: PipelineState; can: (permission: Permission) => boolean }) {
  const { t, text } = usePipelineText();
  const act = usePipelineAction();
  const action = state.nextAction;
  const needed = KIND_PERMISSION[action.kind];
  const memberBlocked = needed !== undefined && !can(needed);
  const onClick = () => act.execute(action);
  // Label/explanation are keyed by the action kind; `wait` shows the active run, `review` the
  // review stage's own detail (that is what the API puts there too).
  const entity = action.entityType ? t(`entityPlural.${action.entityType}`) : '';
  const label =
    action.kind === 'wait'
      ? text(state.activeRun?.labelMsg, state.activeRun?.label ?? action.label)
      : t(`next.${action.kind}.label`, { count: action.params?.count ?? 0, entity });
  const reviewStage = state.stages.find((s) => s.key === 'review' && s.entityType === action.entityType);
  const explanation =
    action.kind === 'review'
      ? text(reviewStage?.detailMsgs, action.explanation)
      : t(`next.${action.kind}.explanation`, { entity });
  const progress = text(state.activeRun?.progressMsg, state.activeRun?.progress ?? '');

  if (action.kind === 'all_done') {
    return (
      <section className="bo-next-step bo-next-step--done" aria-label={t('banner.statusAria')}>
        <div>
          <div className="bo-next-step__kicker">{t('banner.kickerDone')}</div>
          <h2>{label}</h2>
          <p>{explanation}</p>
        </div>
        <span className="bo-next-step__mark" aria-hidden="true">
          <Icon name="check" />
        </span>
      </section>
    );
  }

  return (
    <section className="bo-sections" aria-label={t('banner.nextAria')}>
      <div className="bo-next-step">
        <div>
          <div className="bo-next-step__kicker">{t('banner.kicker')}</div>
          <h2>{label}</h2>
          <p>{explanation}</p>
          {memberBlocked && needed && (
            <p className="bo-next-step__hint">
              {t('banner.needsPermission', { permission: t(`common:permission.${needed}`) })}
            </p>
          )}
          <p className="bo-next-step__hint">{t('banner.suggestedOrder')}</p>
        </div>
        <div className="bo-next-step__actions">
          {action.kind === 'wait' ? (
            <Spinner className="bo-running--on-dark">
              {progress ? <span className="bo-mono">{progress}</span> : t('banner.running')}
            </Spinner>
          ) : memberBlocked ? null : (
            <HpButton
              variant="on-dark"
              icon="play"
              busy={act.isBusy(action)}
              disabled={act.isPending}
              onClick={onClick}
            >
              {act.isBusy(action) ? t('banner.working') : label}
            </HpButton>
          )}
        </div>
      </div>
      <ErrorAlert error={act.error} />
    </section>
  );
}
